import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, User, Phone, Home, Calendar, GraduationCap, Mail } from "lucide-react";

export default function RequestsTab({ onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pending-users");
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const approve = async (userId, name) => {
    try {
      await api.post(`/admin/users/${userId}/approve`);
      toast.success(`${name} approved`);
      load();
      onChange?.();
    } catch {
      toast.error("Failed to approve");
    }
  };

  const reject = async (userId, name) => {
    if (!window.confirm(`Reject and delete ${name}'s request? This can't be undone.`)) return;
    try {
      await api.post(`/admin/users/${userId}/reject`);
      toast.success("Request rejected");
      load();
      onChange?.();
    } catch {
      toast.error("Failed to reject");
    }
  };

  return (
    <div data-testid="requests-tab">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="font-heading font-black text-2xl tracking-tighter">
            Pending enrollment requests
          </h3>
          <p className="text-sm font-medium text-[#4A4A4A]">
            New students self-registering land here. Approve to grant login access; reject to delete the request.
          </p>
        </div>
        <span
          data-testid="pending-count-badge"
          className="inline-flex items-center gap-2 px-3 py-1 border-2 border-[#0A0A0A] rounded-full bg-[#FF4D4D] text-white text-xs font-bold uppercase tracking-widest"
        >
          {rows.length} pending
        </span>
      </div>

      {loading && (
        <div className="brutal-card p-8 text-center font-medium text-[#4A4A4A]">Loading...</div>
      )}
      {!loading && rows.length === 0 && (
        <div className="brutal-card p-8 text-center font-medium text-[#4A4A4A]">
          No pending requests. New enrollments will show up here.
        </div>
      )}

      <div className="space-y-4">
        {rows.map((u) => {
          const s = u.student || {};
          return (
            <div key={u.id} className="brutal-card p-6 bg-white" data-testid={`pending-user-${u.id}`}>
              <div className="grid grid-cols-1 md:grid-cols-[auto,1fr,auto] gap-6 items-start">
                <div className="flex-shrink-0">
                  {s.photo_path ? (
                    <img
                      src={fileUrl(s.photo_path)}
                      alt={u.name}
                      className="w-24 h-24 object-cover border-2 border-[#0A0A0A] rounded-xl"
                    />
                  ) : (
                    <div className="w-24 h-24 flex items-center justify-center border-2 border-[#0A0A0A] rounded-xl bg-[#FFE600]">
                      <User size={40} strokeWidth={2} />
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <h4 className="font-heading font-black text-xl">
                    {s.surname ? `${s.surname} ${u.name}` : u.name}
                  </h4>
                  <div className="mt-1 flex items-center gap-2 text-xs font-medium text-[#4A4A4A]">
                    <Mail size={12} /> {u.email}
                  </div>
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                    <MiniField icon={Phone} label="Contact" value={s.contact_number} />
                    <MiniField icon={Phone} label="Parent" value={s.parent_contact_number} />
                    <MiniField icon={Home} label="KL ID" value={s.kl_id} />
                    <MiniField icon={GraduationCap} label="Year" value={s.year} />
                    <MiniField icon={GraduationCap} label="Branch" value={s.branch} />
                    <MiniField icon={Calendar} label="DOB" value={s.date_of_birth} />
                  </div>
                  {s.residential_address && (
                    <div className="mt-2 text-xs font-medium text-[#4A4A4A]">
                      <span className="font-bold uppercase tracking-widest">Address: </span>
                      {s.residential_address}
                    </div>
                  )}
                  {(s.aadhar_path || s.university_id_path) && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {s.aadhar_path && (
                        <a
                          href={fileUrl(s.aadhar_path)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs font-bold px-3 py-1 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] hover:shadow-brutal-sm transition-shadow"
                        >
                          View Aadhar
                        </a>
                      )}
                      {s.university_id_path && (
                        <a
                          href={fileUrl(s.university_id_path)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs font-bold px-3 py-1 border-2 border-[#0A0A0A] rounded-full bg-[#00E5FF] hover:shadow-brutal-sm transition-shadow"
                        >
                          View University ID
                        </a>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => approve(u.id, u.name)}
                    data-testid={`approve-${u.id}`}
                    className="inline-flex items-center gap-2 font-bold text-sm px-4 py-2 border-2 border-[#0A0A0A] rounded-full bg-[#00CC66] text-white shadow-brutal-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-transform"
                  >
                    <Check size={14} strokeWidth={2.5} /> Approve
                  </button>
                  <button
                    onClick={() => reject(u.id, u.name)}
                    data-testid={`reject-${u.id}`}
                    className="inline-flex items-center gap-2 font-bold text-sm px-4 py-2 border-2 border-[#0A0A0A] rounded-full bg-[#FF4D4D] text-white shadow-brutal-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-transform"
                  >
                    <X size={14} strokeWidth={2.5} /> Reject
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MiniField({ icon: Icon, label, value }) {
  return (
    <div className="border-2 border-[#0A0A0A] rounded-lg p-2 bg-[#FFFCE0]">
      <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest">
        <Icon size={10} strokeWidth={2.5} /> {label}
      </div>
      <div className="font-bold text-xs mt-0.5 truncate">{value || "—"}</div>
    </div>
  );
}
